import { MainCategories, PlayerSubcategories, PropertyKeys } from '../../../api/analytics/ClientEvents';
import { v4 as uuidV4 } from 'uuid';
import { addProperty } from '../../../api/analytics/AnalyticsUtil';
import { GlobalSettingsKeys } from '../../../api/models/GlobalSettingsKeys';
import { Log } from '@lightningjs/sdk';
import { Backend } from '../../../api/Backend';
import { IProperty } from '../../../api/models/AndromeTV.types';

/**
 * @enum {string}
 */
export const ErrOriginator = {
  StartStream: 'StartStream',
  StopStream: 'StopStream',
  Player: 'Player',
};

export const enum PLAYER_STOP_REASON {
  USER_INITIATED = 'USER_INITIATED',
  FINISHED = 'FINISHED',
  SCHEDULE_END = 'SCHEDULE_END',
  RESTART = 'RESTART',
  ERROR = 'ERROR',
  NO_INTERACTION = 'NO_INTERACTION',
}

export class AnalyticsHandler {
  private _backend: Backend;
  private referenceId: string | undefined;
  private playerStartTime: number | undefined;

  constructor(backend: Backend) {
    this._backend = backend;
  }

  logPlayStart() {
    this.referenceId = uuidV4();
    this.playerStartTime = Date.now();
    const excluded = [PropertyKeys.streamId, PropertyKeys.manifest, PropertyKeys.timePlayed, PropertyKeys.originator];
    const properties = [];
    const analyticsProps = this._backend.playerController.streamInfo?.generateAnalyticsProperties(excluded);
    if (analyticsProps) properties.push(...analyticsProps);
    const analyticsPropsManager = this._backend.playerController.streamManager?.analyticsProperties(excluded);
    if (analyticsPropsManager) properties.push(...analyticsPropsManager);
    addProperty(PropertyKeys.referenceId, this.referenceId, excluded, properties);

    this._backend.logService.createClientEventEntry(
      { type: MainCategories.PLAYER, subType: PlayerSubcategories.PLAY_START },
      properties
    );
  }

  logPlayStop(reason?: PLAYER_STOP_REASON) {
    const excluded = [PropertyKeys.originator];
    const timePlayed = this.playerStartTime ? Date.now() - this.playerStartTime : undefined;
    const properties = [];
    const analyticsProps = this._backend.playerController.streamInfo?.generateAnalyticsProperties(excluded);
    if (analyticsProps) properties.push(...analyticsProps);
    const analyticsPropsManager = this._backend.playerController.streamManager?.analyticsProperties(excluded);
    if (analyticsPropsManager) properties.push(...analyticsPropsManager);
    properties.push(...this._backend.playerController.playerService.DRMAnalyticsProperties());
    addProperty(PropertyKeys.referenceId, this.referenceId, excluded, properties);
    addProperty(PropertyKeys.timePlayed, timePlayed, excluded, properties);
    if (reason) addProperty(PropertyKeys.reason, reason, excluded, properties);

    this._backend.logService.createClientEventEntry(
      { type: MainCategories.PLAYER, subType: PlayerSubcategories.PLAY_STOP },
      properties
    );
  }

  /**
   * @param err
   * @param {ErrOriginator} originator
   */
  logPlayError(err: { detail: any; code: any; message: any }, originator: any) {
    const excluded = [PropertyKeys.timePlayed];
    const properties = [];
    const analyticsProps = this._backend.playerController.streamInfo?.generateAnalyticsProperties(excluded);
    if (analyticsProps) properties.push(...analyticsProps);
    const analyticsPropsManager = this._backend.playerController.streamManager?.analyticsProperties(excluded);
    if (analyticsPropsManager) properties.push(...analyticsPropsManager);
    addProperty(PropertyKeys.referenceId, this.referenceId, excluded, properties);
    addProperty(PropertyKeys.originator, originator, excluded, properties);
    this._addErrorProperties(err, properties, excluded);

    this._backend.logService.createClientEventEntry(
      { type: MainCategories.PLAYER, subType: PlayerSubcategories.PLAY_ERROR },
      properties
    );
  }

  logKeepAliveError(err: { detail: any; code: any; message: any }) {
    const excluded = [
      PropertyKeys.season,
      PropertyKeys.episode,
      PropertyKeys.name,
      PropertyKeys.stackTrace,
      PropertyKeys.originator,
    ];
    const properties = [];
    const timePlayed = this.playerStartTime ? Date.now() - this.playerStartTime : undefined;
    const analyticsProps = this._backend.playerController.streamInfo?.generateAnalyticsProperties(excluded);
    if (analyticsProps) properties.push(...analyticsProps);
    const analyticsPropsManager = this._backend.playerController.streamManager?.analyticsProperties(excluded);
    if (analyticsPropsManager) properties.push(...analyticsPropsManager);
    addProperty(PropertyKeys.referenceId, this.referenceId, excluded, properties);
    addProperty(PropertyKeys.timePlayed, timePlayed, excluded, properties);
    this._addErrorProperties(err, properties, excluded);

    this._backend.logService.createClientEventEntry(
      { type: MainCategories.PLAYER, subType: PlayerSubcategories.KEEP_ALIVE_ERROR },
      properties
    );
  }

  _addErrorProperties(err: { detail: any; code: any; message: any }, properties: IProperty[], excluded: string[]) {
    const stackTraceMaxLines =
      this._backend.globalSettings.numberValue(GlobalSettingsKeys.stackTraceMaximumLines) || 25;
    let stackTrace;
    try {
      stackTrace = Error().stack?.split('\n').slice(0, stackTraceMaxLines).join('\n');
      addProperty(PropertyKeys.stackTrace, stackTrace, excluded, properties);
    } catch (err) {
      Log.info('no stacktrace');
    }

    if (err.detail) {
      err = err.detail;
      addProperty(PropertyKeys.errorCode, err.code ? `PLAYER_ERROR ${err.code || ''}` : '', excluded, properties);
    } else {
      addProperty(PropertyKeys.errorCode, err.code, excluded, properties);
    }

    addProperty(PropertyKeys.errorMessage, err.message, excluded, properties);
  }
}
